import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import LytIcon from 'src/components/library/icon/LytIcon';
import LytTooltip from 'src/components/library/tooltip/LytTooltip';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from 'src/components/styled/theme/typography-utils';
const MenuItemContainer = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        !['disabled', 'danger', 'selected', 'backgroundColor'].includes(prop),
})`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)};
    display: flex;
    flex-direction: row;
    padding: 8px 16px;
    align-items: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background-color: ${({ theme, selected, backgroundColor, disabled }) =>
        disabled
            ? theme.components.lyt_menu_item.background.disabled
            : selected
            ? theme.components.lyt_menu_item.background.selected
            : backgroundColor
            ? backgroundColor
            : theme.components.lyt_menu_item.background.default};
    color: ${({ theme, selected, disabled, danger }) =>
        disabled
            ? theme.components.lyt_menu_item.color.disabled
            : danger
            ? theme.components.lyt_menu_item.color.danger
            : selected
            ? theme.components.lyt_menu_item.color.selected
            : theme.components.lyt_menu_item.color.default};
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

    /* Make focusable for keyboard navigation */
    &:focus-visible {
        outline: 2px solid
            ${({ theme }) => theme.components.lyt_menu_item.outline.focus};
        outline-offset: -1px;
        background-color: ${({ theme, selected }) =>
            selected
                ? theme.components.lyt_menu_item.background.selectedFocus
                : theme.components.lyt_menu_item.background.focus};
    }

    &:hover {
        background-color: ${({ theme, selected, disabled }) =>
            disabled
                ? theme.components.lyt_menu_item.background.disabled
                : selected
                ? theme.components.lyt_menu_item.background.selectedHover
                : theme.components.lyt_menu_item.background.hover};
    }
    &:active {
        background-color: ${({ theme, selected, disabled }) =>
            disabled
                ? theme.components.lyt_menu_item.background.disabled
                : selected
                ? theme.components.lyt_menu_item.background.selectedPressed
                : theme.components.lyt_menu_item.background.pressed};
    }
    border-left: 2px solid
        ${({ theme, selected }) =>
            selected
                ? theme.components.lyt_menu_item.border.selected
                : 'transparent'};
    > svg {
        margin-right: 8px;
    }
`;

/**
 * @description The MenuItem component
 * @param {Object} props - The component props
 * @param {string} props.id - The id of the menu item, also rendered as `data-option-id`
 *   (not `id`: a multiselect option's checkbox already uses the same value as its DOM id)
 * @param {object} props.children - The children of the menu item
 * @param {Function} props.onClick - The function to handle the click event
 * @param {string} props.iconName - The name of the icon to display
 * @param {boolean} props.disabled - Whether the menu item is disabled
 * @param {boolean} props.danger - Whether the menu item is a danger/destructive action (renders in red)
 * @param {string} props.tooltip - Tooltip text to show on hover (especially useful for disabled items)
 */
const LytMenuItem = ({
    id,
    children,
    onClick,
    selected = false,
    iconName = null,
    backgroundColor,
    disabled = false,
    danger = false,
    tooltip = null,
}) => {
    /**
     * @description Handles the click event for the menu item
     */
    const handleClick = () => {
        if (disabled) return;
        onClick(id);
    };

    /**
     * @description Handles keyboard events for accessibility
     * @param {KeyboardEvent} event - The keyboard event
     */
    const handleKeyDown = (event) => {
        if (disabled) return;
        // Trigger click on Enter or Space key
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(id);
        }
    };
    const theme = useTheme();
    const menuItemIcon = theme?.components?.lyt_menu_item?.icon;
    const iconColor = disabled
        ? menuItemIcon?.disabled
        : danger
        ? menuItemIcon?.danger
        : selected
        ? menuItemIcon?.selected
        : menuItemIcon?.default;
    const menuItem = (
        <MenuItemContainer
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            selected={selected}
            backgroundColor={backgroundColor}
            disabled={disabled}
            danger={danger}
            tabIndex={disabled ? -1 : 0}
            role="menuitem"
            data-option-id={id}
            aria-disabled={disabled}
            className={selected ? 'selected' : ''}>
            {iconName && <LytIcon name={iconName} color={iconColor} />}
            {children}
        </MenuItemContainer>
    );

    // Wrap with tooltip if provided
    if (tooltip) {
        return <LytTooltip content={tooltip}>{menuItem}</LytTooltip>;
    }
    return menuItem;
};
LytMenuItem.propTypes = {
    id: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
    selected: PropTypes.bool,
    iconName: PropTypes.string,
    backgroundColor: PropTypes.string,
    disabled: PropTypes.bool,
    danger: PropTypes.bool,
    tooltip: PropTypes.string,
};
export default LytMenuItem;
